import React, { useState, useRef, useEffect, cloneElement, useCallback } from 'react';
import { Calendar, X, Loader2, User, Clock, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useAuthStore } from '../../store/authStore';
import { MessageService } from '../../services/messageService';
// import { toast } from './Toaster';
import ProfileAvatar from './ProfileAvatar';
import FullProfileModal from '../modals/FullProfileModal';

interface UserProfilePopoverProps {
  userId: number;
  trigger: React.ReactElement;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  disabled?: boolean;
  className?: string;
}

interface PopoverPosition {
  top: number;
  left: number;
  placement: 'top' | 'bottom' | 'left' | 'right';
}

const UserProfilePopover: React.FC<UserProfilePopoverProps> = ({
  userId,
  trigger,
  placement = 'auto',
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFullModalOpen, setIsFullModalOpen] = useState(false);
  const [position, setPosition] = useState<PopoverPosition>({ top: 0, left: 0, placement: 'bottom' });
  const [canMessage, setCanMessage] = useState<boolean | null>(null);
  const [isCheckingMessage, setIsCheckingMessage] = useState(false);
  const triggerRef = useRef<HTMLElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const { profile, isLoading, error, fetchProfile, clearProfile } = useUserProfile();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  // Calculate optimal popover position
  const calculatePosition = useCallback((): PopoverPosition => {
    if (!triggerRef.current) {
      return { top: 0, left: 0, placement: 'bottom' };
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const popoverWidth = 320; // Fixed width for consistency
    const popoverHeight = 400; // Approximate height
    const margin = 8; // Margin from viewport edges

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let finalPlacement: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
    let top = 0;
    let left = 0;

    if (placement === 'auto') {
      // Determine best placement based on available space
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const spaceRight = viewportWidth - triggerRect.right;
      const spaceLeft = triggerRect.left;

      if (spaceBelow >= popoverHeight + margin) {
        finalPlacement = 'bottom';
      } else if (spaceAbove >= popoverHeight + margin) {
        finalPlacement = 'top';
      } else if (spaceRight >= popoverWidth + margin) {
        finalPlacement = 'right';
      } else if (spaceLeft >= popoverWidth + margin) {
        finalPlacement = 'left';
      } else {
        // Not enough space anywhere, use bottom and let it scroll
        finalPlacement = 'bottom';
      }
    } else {
      finalPlacement = placement;
    }

    // Calculate position based on placement
    switch (finalPlacement) {
      case 'top':
        top = triggerRect.top - popoverHeight - margin;
        left = triggerRect.left + (triggerRect.width - popoverWidth) / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + margin;
        left = triggerRect.left + (triggerRect.width - popoverWidth) / 2;
        break;
      case 'left':
        top = triggerRect.top + (triggerRect.height - popoverHeight) / 2;
        left = triggerRect.left - popoverWidth - margin;
        break;
      case 'right':
        top = triggerRect.top + (triggerRect.height - popoverHeight) / 2;
        left = triggerRect.right + margin;
        break;
    }

    // Ensure popover stays within viewport bounds
    left = Math.max(margin, Math.min(left, viewportWidth - popoverWidth - margin));
    top = Math.max(margin, Math.min(top, viewportHeight - popoverHeight - margin));

    return { top, left, placement: finalPlacement };
  }, [placement]);

  // Check if users can message each other
  const checkCanMessage = async () => {
    if (!user || user.id === userId) {
      setCanMessage(false);
      return;
    }

    try {
      setIsCheckingMessage(true);
      const result = await MessageService.canMessageUser(userId.toString());
      setCanMessage(result.canMessage);
    } catch (error) {
      console.error('Failed to check message permission:', error);
      setCanMessage(false);
    } finally {
      setIsCheckingMessage(false);
    }
  };

  // Handle message button click
  const handleMessageClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    handleClose();
    navigate(`/messages?user=${userId}`);
  };

  // Handle popover open
  const handleOpen = async () => {
    if (disabled) return;
    
    setIsOpen(true);
    setPosition(calculatePosition());
    
    // Fetch profile data if not already loaded
    if (!profile || profile.id !== userId) {
      await fetchProfile(userId);
    }

    // Check message permission
    await checkCanMessage();
  };

  // Handle popover close
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      clearProfile();
    }, 200); // Delay to allow for smooth animation
  }, [clearProfile]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        popoverRef.current &&
        triggerRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleClose]);

  // Update position on window resize
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        setPosition(calculatePosition());
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, calculatePosition]);

  // Format display values
  const formatGender = (gender: string) => {
    const genderMap: { [key: string]: string } = {
      // New Dutch gender options
      'MAN': 'Man',
      'VROUW': 'Vrouw',
      'TRANSGENDER': 'Transgender',
      'NON_BINAIR': 'Non-binair',
      'GENDERFLUIDE': 'Genderfluide',
      'ANDERS': 'Anders (leg uit in bio)',
      'LIEVER_NIET': 'Zeg ik liever niet',
      // Backwards compatibility for lowercase versions
      'man': 'Man',
      'vrouw': 'Vrouw',
      'transgender': 'Transgender',
      'non_binair': 'Non-binair',
      'genderfluide': 'Genderfluide',
      'anders': 'Anders (leg uit in bio)',
      'liever_niet': 'Zeg ik liever niet',
      'zeg_ik_liever_niet': 'Zeg ik liever niet',
    };
    return genderMap[gender] || gender;
  };

  // Clone trigger with click handler
  const enhancedTrigger = cloneElement(trigger, {
    ref: triggerRef,
    onClick: (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (trigger.props.onClick) {
        trigger.props.onClick(e);
      }
      handleOpen();
    },
    style: {
      ...trigger.props.style,
      cursor: disabled ? 'default' : 'pointer',
    },
  });

  return (
    <>
      {enhancedTrigger}

      {/* Popover */}
      {isOpen && (
        <div
          ref={popoverRef}
          className={`fixed z-50 w-80 bg-surface border border-white/[.09] rounded-card shadow-panel transition-all duration-200 ${className}`}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: 'scale(1)',
            opacity: 1,
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/[.08]">
            <div className="flex items-center space-x-3">
              {profile && (
                <>
                  <ProfileAvatar user={profile} size="lg" />
                  <h3 className="text-sm font-semibold text-fg-bright">{profile.username}</h3>
                </>
              )}
              {!profile && !isLoading && !error && (
                <h3 className="text-sm font-semibold text-fg-bright">Profile</h3>
              )}
            </div>
            <button
              onClick={handleClose}
              className="p-1 text-fg-muted hover:text-fg-bright rounded transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-neon-cyan" />
                <span className="ml-2 text-sm text-fg-muted">Loading profile...</span>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <p className="text-sm text-neon-pink-soft mb-2">Failed to load profile</p>
                  <button
                    onClick={() => fetchProfile(userId)}
                    className="sf-link text-xs"
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}

            {profile && !isLoading && !error && (
              <div className="space-y-3">
                {/* Age */}
                {profile.age && (
                  <div className="flex items-center text-sm text-fg-body">
                    <Calendar className="h-4 w-4 mr-3 text-neon-cyan" />
                    <span>{profile.age} years old</span>
                  </div>
                )}

                {/* Gender */}
                {profile.gender && (
                  <div className="flex items-center text-sm text-fg-body">
                    <User className="h-4 w-4 mr-3 text-neon-cyan" />
                    <span>{formatGender(profile.gender)}</span>
                  </div>
                )}


                {/* Member Since */}
                {profile.createdAt && (
                  <div className="flex items-center text-sm text-fg-body">
                    <Clock className="h-4 w-4 mr-3 text-neon-cyan" />
                    <span>
                      Lid sinds {new Date(profile.createdAt).toLocaleDateString('nl-NL', {
                        year: 'numeric',
                        month: 'long',
                      })}
                    </span>
                  </div>
                )}

                {/* Actions */}
                <div className="pt-3 border-t border-white/[.08]">
                  {user && user.id !== userId ? (
                    <div className="flex gap-2">
                      {/* Message Button */}
                      {isCheckingMessage ? (
                        <div className="flex items-center justify-center flex-1 px-3 py-2 text-sm text-fg-muted bg-white/[.04] rounded-btn">
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          <span className="text-xs">Laden...</span>
                        </div>
                      ) : canMessage ? (
                        <button
                          onClick={handleMessageClick}
                          className="sf-btn-primary flex-1 px-3 py-2 text-sm"
                        >
                          <MessageCircle className="h-4 w-4 mr-1" />
                          <span>Berichten</span>
                        </button>
                      ) : canMessage === false ? (
                        <div className="flex items-center justify-center flex-1 px-3 py-2 text-xs text-fg-meta bg-white/[.04] rounded-btn">
                          <MessageCircle className="h-3 w-3 mr-1 opacity-50" />
                          <span>Niet beschikbaar</span>
                        </div>
                      ) : null}

                      {/* View Full Profile Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsFullModalOpen(true);
                          handleClose(); // Close the popover
                        }}
                        className="sf-btn-secondary flex-1 px-3 py-2 text-sm"
                      >
                        <User className="h-4 w-4 mr-1" />
                        <span>Bekijk profiel</span>
                      </button>
                    </div>
                  ) : (
                    /* Just profile button for own profile */
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsFullModalOpen(true);
                        handleClose(); // Close the popover
                      }}
                      className="sf-btn-secondary w-full px-3 py-2 text-sm"
                    >
                      <User className="h-4 w-4 mr-1" />
                      <span>Bekijk profiel</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full Profile Modal */}
      <FullProfileModal
        isOpen={isFullModalOpen}
        onClose={() => setIsFullModalOpen(false)}
        userId={userId}
      />
    </>
  );
};

export default UserProfilePopover;